import {
  useEffect,
  useReducer,
  useRef,
  useState,
  type ReactNode,
  type RefObject,
} from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Alert, Box, Button, Skeleton, Text } from '@citibox-panel/ui';
import { ApiError, type BoxMarks } from '@citibox-panel/api-client';
import { boxMarksCase, MAX_MARKS } from '../lib/boxMarksCase';
import { boxMarksError } from '../lib/boxMarksError';
import { useReportFailure } from '../../search-shell/hooks/useErrorNotice';
import { useBoxMarks } from '../hooks/useBoxMarks';
import { useClearBoxMarks } from '../hooks/useClearBoxMarks';
import { useAutoDismiss } from '../hooks/useAutoDismiss';
import {
  deliveryActionFlow,
  initialDeliveryActionState,
  type DeliveryActionState,
} from '../model/deliveryActionFlow';
import { BoxMarksConfirmPanel } from './BoxMarksConfirmPanel';

export interface BoxMarksBannerProps {
  open: boolean;
  boxId: string;
  fetchBoxMarks: (boxId: string) => Promise<BoxMarks>;
  clearBoxMarks: (boxId: string) => Promise<void>;
}

// One line of copy: the skeleton holds exactly the notice's footprint, or the slot changes height
// between states and shoves the carriles down under a cursor already on its way to one.
const SLOT_HEIGHT = 40;

// TODO: i18n — copy en ES hasta Fase 1.
const TRIGGER_LABEL = 'Ya sincronizado';
const READ_ERROR = 'No se han podido cargar las marcas del buzón.';
const CLEARED = 'Marcas limpiadas';
const NOTICE_ID = 'clear-box-marks';
const BOX02_MESSAGE =
  'El buzón está inutilizado (BOX02). Las marcas ya no pueden limpiarse desde el panel — es un caso de Mantenimiento.';

function Slot({ children }: { children: ReactNode }) {
  // No bottom padding: the codes block below opens with its own, and adding one here would stack
  // two gaps between this and the carriles.
  return <Box sx={{ px: 2, pt: 2 }}>{children}</Box>;
}

// A resolved notice filling the slot. Three of the states differ only by tone and sentence.
function NoticeSlot({
  severity,
  children,
}: {
  severity: 'success' | 'error' | 'warning';
  children: ReactNode;
}) {
  return (
    <Slot>
      <Alert variant="notice" severity={severity}>
        {children}
      </Alert>
    </Slot>
  );
}

// Deliberately quieter than the notices it sits among: until app-service ships the read this is what
// EVERY «buzón» shows on every unfold, and red there would read as a problem with the delivery
// rather than with the panel. Holds the notice's footprint so the slot never changes height.
function ReadErrorStrip({ onRetry }: { onRetry: () => void }) {
  return (
    <Slot>
      <Box
        role="status"
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
          minHeight: SLOT_HEIGHT,
          border: 1,
          borderColor: 'divider',
          borderRadius: 2,
          bgcolor: 'background.paper',
          pl: 2,
          pr: 1,
        }}
      >
        <Text variant="body2" color="secondary">
          {READ_ERROR}
        </Text>
        <Button variant="text" onClick={onRetry}>
          Reintentar
        </Button>
      </Box>
    </Slot>
  );
}

// Mounted whenever the delivery has a «buzón» (even folded), so the query keeps its cache across a
// fold→unfold; the hook is open-gated, so opening the detail costs no request. Renders nothing until
// unfolded.
//
// The flow state deliberately lives BELOW this gate, in the region: folding must discard a
// half-composed confirmation, and making that structural beats trusting anyone to remember it.
export function BoxMarksBanner({ open, boxId, fetchBoxMarks, clearBoxMarks }: BoxMarksBannerProps) {
  const { marks, isPending, isFetching, isError, refetch } = useBoxMarks(boxId, open, fetchBoxMarks);
  if (!open) return null;
  return (
    <BoxMarksRegion
      marks={marks}
      isPending={isPending}
      isFetching={isFetching}
      isError={isError}
      refetch={refetch}
      boxId={boxId}
      clearBoxMarks={clearBoxMarks}
    />
  );
}

interface BoxMarksRegionProps {
  marks: BoxMarks | undefined;
  isPending: boolean;
  isFetching: boolean;
  isError: boolean;
  refetch: () => void;
  boxId: string;
  clearBoxMarks: (boxId: string) => Promise<void>;
}

function BoxMarksRegion({
  marks,
  isPending,
  isFetching,
  isError,
  refetch,
  boxId,
  clearBoxMarks,
}: BoxMarksRegionProps) {
  const [flow, dispatch] = useReducer(deliveryActionFlow, initialDeliveryActionState);
  const [refocusTrigger, setRefocusTrigger] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const queryClient = useQueryClient();
  const mutation = useClearBoxMarks(boxId, clearBoxMarks);

  // A refused clear FLOATS (ADR 0021): it is a write failure, unlike the READ failure below, which
  // keeps its neutral strip. Reporting hands the flow back to idle, so the slot returns to the
  // level and its «Ya sincronizado» trigger while the notice stays up until the agent dismisses it
  // — which is what the old `persistent` axis bought, for the codes that never arrived.
  useReportFailure(
    NOTICE_ID,
    flow.phase === 'error' ? boxMarksError(flow.errorCode) : null,
    () => {
      dispatch({ type: 'DISMISS' });
    },
  );

  // Gated on the banner being ON SCREEN, not merely on the phase: the success path re-reads the
  // level, and starting the countdown behind that would spend it on a banner nobody saw.
  useAutoDismiss(flow.phase === 'success' && !isFetching, dispatch);

  // The trigger stops existing while the panel is up, so the ref is empty until it comes back:
  // focus after the re-render, not during the cancel.
  useEffect(() => {
    if (!refocusTrigger) return;
    triggerRef.current?.focus();
    setRefocusTrigger(false);
  }, [refocusTrigger]);

  if (isPending) {
    return (
      <Slot>
        <Skeleton variant="rounded" width="100%" height={SLOT_HEIGHT} />
      </Slot>
    );
  }

  if (isError) {
    return <ReadErrorStrip onRetry={refetch} />;
  }

  const mark = marks?.mark ?? 0;
  const kind = boxMarksCase(mark);

  // The flow outlives the level it acted on: a successful clear brings the level back at zero, and
  // unmounting on that would destroy the confirmation before the agent could read it.
  if (kind === 'none' && flow.phase === 'idle') return null;

  if (flow.phase === 'success') {
    return <NoticeSlot severity="success">{CLEARED}</NoticeSlot>;
  }

  if (kind === 'box02') {
    return <NoticeSlot severity="error">{BOX02_MESSAGE}</NoticeSlot>;
  }

  const onConfirm = () => {
    dispatch({ type: 'SUBMIT' });
    mutation.mutate(undefined, {
      onSuccess: () => {
        dispatch({ type: 'SUCCESS' });
      },
      onError: (error: unknown) => {
        dispatch({ type: 'ERROR', code: error instanceof ApiError ? error.code : null });
        // Banner and re-read are one policy, so they live together rather than in the hook: a
        // refused clear must leave the level on screen matching the server.
        void queryClient.invalidateQueries({ queryKey: ['box-marks', boxId] });
      },
    });
  };

  return (
    <MarksNotice
      mark={mark}
      flow={flow}
      triggerRef={triggerRef}
      onStart={() => {
        dispatch({ type: 'START', action: 'clearBoxMarks' });
      }}
      onCancel={() => {
        dispatch({ type: 'CANCEL' });
        setRefocusTrigger(true);
      }}
      onConfirm={onConfirm}
    />
  );
}

interface MarksNoticeProps {
  mark: number;
  flow: DeliveryActionState;
  triggerRef: RefObject<HTMLButtonElement | null>;
  onStart: () => void;
  onCancel: () => void;
  onConfirm: () => void;
}

// The level, and the two-step clear hung off it. The trigger rides the alert's action slot and the
// confirm panel sits BELOW the alert, never inside it: the level must stay legible while it is being
// confirmed, and the alert is an assertive live region that has no business wrapping controls.
function MarksNotice({
  mark,
  flow,
  triggerRef,
  onStart,
  onCancel,
  onConfirm,
}: MarksNoticeProps) {
  return (
    <Slot>
      <Alert
        variant="notice"
        severity="warning"
        {...(flow.phase === 'idle'
          ? {
              action: (
                <Button ref={triggerRef} variant="contained" size="small" onClick={onStart}>
                  {TRIGGER_LABEL}
                </Button>
              ),
            }
          : {})}
      >
        <Text variant="body2" component="span">
          Marcas del buzón:{' '}
        </Text>
        <Text variant="body2" component="span" bold>
          {mark}/{MAX_MARKS}
        </Text>
      </Alert>
      <BoxMarksConfirmPanel state={flow} mark={mark} onCancel={onCancel} onConfirm={onConfirm} />
    </Slot>
  );
}
